import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Package } from 'lucide-react';
import { Modal, Button } from '../common';
import { Category, SubcategoryHierarchy, CreateProductData } from '../../types';

/* ========================= Helpers ========================= */
const IconAvatar: React.FC<{
  url?: string | null;
  alt: string;
  fallback: React.ReactNode;
  size?: number;
  className?: string;
}> = ({ url, alt, fallback, size = 36, className }) => {
  const [errored, setErrored] = React.useState(false);
  const valid = typeof url === 'string' && url.trim().length > 0 && !errored;

  return (
    <div className={`inline-flex items-center gap-2 ${className || ''}`}>
      <div
        className="grid place-items-center rounded-md overflow-hidden border border-gray-200 bg-white"
        style={{ width: size, height: size }}
      >
        {valid ? (
          <img
            src={url as string}
            alt={alt}
            className="w-full h-full object-contain"
            onError={() => setErrored(true)}
          />
        ) : (
          <div className="text-gray-500">{fallback}</div>
        )}
      </div>
    </div>
  );
};

const getCategoryIconUrl = (cat: any) => cat?.iconUrl ?? cat?.icon_url ?? null;

/* ========================= Tipos form ========================= */
export type ProductFormData = {
  name: string;
  description?: string;
  internalCode?: string;
  subcategoryId: string;
  imageUrl?: string;
  imageFile?: FileList;
};

interface CreateProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: Category[];
  allSubcategories: Array<
    SubcategoryHierarchy & { categoryName: string; categoryId: string; level?: number }
  >;
  onSubmit: (data: CreateProductData) => Promise<void>;
  isSubmitting: boolean;
}

/* ======================== Modal Producto ======================= */
export const CreateProductModal: React.FC<CreateProductModalProps> = ({
  isOpen,
  onClose,
  categories,
  allSubcategories,
  onSubmit,
  isSubmitting
}) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<ProductFormData>();

  // === Preview y validación de imagen ===
  const imageFileList = watch('imageFile');
  const selectedFile = imageFileList && imageFileList.length > 0 ? imageFileList[0] : null;
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        alert('El archivo supera el límite de 5 MB. Selecciona una imagen más liviana.');
        setPreviewUrl(null);
        return;
      }
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [selectedFile]);

  // Filtrar subcategorías según la categoría
  const availableSubcategories = useMemo(() => {
    if (!selectedCategoryId) return [];
    return allSubcategories.filter(sub => sub.categoryId === selectedCategoryId);
  }, [selectedCategoryId, allSubcategories]);

  // Categoría seleccionada (para mostrar ícono)
  const selectedCategory = useMemo(
    () => categories.find(c => c.id === selectedCategoryId),
    [categories, selectedCategoryId]
  );

  const onSubmitForm = async (data: ProductFormData) => {
    try {
      const file = data.imageFile && data.imageFile.length > 0 ? data.imageFile[0] : null;

      const submitData: CreateProductData = {
        name: data.name,
        description: data.description,
        internalCode: data.internalCode,
        subcategoryId: data.subcategoryId,
        imageFile: file ?? undefined,
        imageUrl: file ? undefined : data.imageUrl,
      };

      // console.log('[CreateProductModal] submitData:', {
      //   ...submitData,
      //   imageFile: file ? { name: file.name, size: file.size, type: file.type } : null
      // });

      await onSubmit(submitData);
      reset();
      setSelectedCategoryId('');
      onClose();
    } catch (error) {
      console.error('[CreateProductModal] Error:', error);
    }
  };

  const handleClose = () => {
    reset();
    setSelectedCategoryId('');
    onClose();
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setValue('subcategoryId', '');
  };

  const renderIndentedOption = (sub: SubcategoryHierarchy & { level?: number }) => {
    const indent = '  '.repeat(sub.level || 0);
    return `${indent}${sub.name} (${sub.size})`;
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose}
      title="Crear Nuevo Producto"
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-6">
        {/* Seleccionar categoría */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">
            Categoría *
          </label>
          <div className="flex items-center justify-between gap-3">
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul focus:border-transparent"
              onChange={(e) => handleCategoryChange(e.target.value)}
              value={selectedCategoryId}
            >
              <option value="">Seleccionar categoría...</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>

            <IconAvatar
              url={getCategoryIconUrl(selectedCategory)}
              alt={selectedCategory ? selectedCategory.name : 'Categoría'}
              fallback={<Package className="w-5 h-5" />}
            />
          </div>
        </div>

        {/* Seleccionar subcategoría */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">
            Subcategoría *
          </label>
          <select
            {...register('subcategoryId', { required: 'La subcategoría es requerida' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul focus:border-transparent"
            disabled={!selectedCategoryId}
          >
            <option value="">
              {selectedCategoryId ? 'Seleccionar subcategoría...' : 'Primero selecciona una categoría'}
            </option>
            {availableSubcategories.map(subcategory => (
              <option key={subcategory.id} value={subcategory.id}>
                {renderIndentedOption(subcategory)}
              </option>
            ))}
          </select>
          {errors.subcategoryId && (
            <p className="mt-1 text-sm text-red-600">{errors.subcategoryId.message}</p>
          )}
        </div>

        {/* Nombre */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">
            Nombre del Producto *
          </label>
          <input
            type="text"
            {...register('name', { 
              required: 'El nombre es requerido',
              minLength: { value: 2, message: 'El nombre debe tener al menos 2 caracteres' }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul focus:border-transparent"
            placeholder="Ej: Blanqueador Premium 500ml"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>

        {/* Código interno */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">
            Código Interno (EAN)
          </label>
          <input
            type="text"
            {...register('internalCode')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul focus:border-transparent"
            placeholder="Ej: BP500, LM1000"
          />
        </div>

        {/* Imagen */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-brinsa-azul mb-2">
              Imagen del Producto (archivo)
            </label>
            <input
              type="file"
              accept="image/*"
              {...register('imageFile')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">
              Tamaño máximo permitido: <span className="font-semibold text-brinsa-azul">5 MB</span>. 
              Formatos recomendados: JPG o PNG.
            </p>

           {previewUrl && (
            <div className="mt-3">
                <div className="text-xs text-brinsa-azulOscuro/70 mb-1">Vista previa:</div>
                <div className="w-full h-36 rounded-lg border border-brinsa-celeste/30 bg-gray-50 flex items-center justify-center overflow-hidden">
                <img
                    src={previewUrl}
                    alt="preview"
                    className="max-w-full max-h-full object-contain transition-transform duration-200 hover:scale-105"
                />
                </div>
            </div>
            )}
          </div>

          {/* URL */}
          <div>
            <label className="block text-sm font-medium text-brinsa-azul mb-2">
              URL de Imagen
            </label>
            <input
              type="url"
              {...register('imageUrl')}
              disabled={!!selectedFile}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              placeholder={selectedFile ? 'Deshabilitado: usar archivo seleccionado' : 'https://ejemplo.com/imagen.jpg'}
            />
            {selectedFile && (
              <p className="mt-1 text-xs text-brinsa-azulOscuro/70">
                Se usará la imagen subida. Si quieres usar una URL, elimina el archivo seleccionado.
              </p>
            )}
          </div>
        </div>

        {/* Descripción */}
        <div>
          <label className="block text-sm font-medium text-brinsa-azul mb-2">
            Descripción
          </label>
          <textarea
            {...register('description')}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brinsa-azul focus:border-transparent"
            placeholder="Descripción detallada del producto..."
          />
        </div>

        {/* Botones */}
        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            isLoading={isSubmitting}
            leftIcon={<Package className="w-4 h-4" />}
          >
            Crear Producto
          </Button>
        </div>
      </form>
    </Modal>
  );
};
